/*
 * Responsive image helpers.
 *
 * Local photos (/img/*.jpg) get WebP variants + a blurred placeholder from
 * `image-manifest.json` (built by tests/build_images.py). Remote photos
 * (Unsplash / Pexels) carry a `w=` parameter we can rewrite per width.
 */
import manifest from "./image-manifest.json";

const REMOTE_WIDTHS = [480, 768, 1080, 1440, 2000];
export const DEFAULT_SIZES = "(min-width: 1024px) 50vw, 100vw";

const hasWidthParam = (url) => /[?&]w=\d+/.test(url);
const withWidth = (url, w) => url.replace(/([?&])w=\d+/, `$1w=${w}`);

/**
 * Returns `{ src, srcSet, sizes, width, height, lqip }` for any image URL.
 * Unknown URLs pass through untouched.
 */
export function imageProps(src, sizes = DEFAULT_SIZES) {
  if (!src || typeof src !== "string") return { src };
  const m = manifest[src];
  if (m) {
    return {
      src: `/img/r/${m.base}-${m.widths[Math.max(0, m.widths.length - 2)]}.webp`,
      srcSet: m.widths.map((w) => `/img/r/${m.base}-${w}.webp ${w}w`).join(", "),
      sizes,
      width: m.width,
      height: m.height,
      lqip: m.lqip,
    };
  }
  if (/^https?:/.test(src) && hasWidthParam(src)) {
    return { src: withWidth(src, 1440), srcSet: REMOTE_WIDTHS.map((w) => `${withWidth(src, w)} ${w}w`).join(", "), sizes };
  }
  return { src };
}

/** `<link rel="preload">` tags for above-the-fold images on a route (idempotent). */
export function preloadImages(list) {
  if (typeof document === "undefined") return;
  for (const { src, sizes } of list) {
    const p = imageProps(src, sizes);
    const key = `preload:${src}`;
    if (document.head.querySelector(`link[data-key="${key}"]`)) continue;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.dataset.key = key;
    link.fetchPriority = "high";
    if (p.srcSet) {
      link.imageSrcset = p.srcSet;
      link.imageSizes = p.sizes;
    } else {
      link.href = p.src;
    }
    document.head.appendChild(link);
  }
}
